import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, ChevronRight } from "lucide-react";
import { findPost, POSTS } from "@/lib/blog";
import { Button } from "@/components/ui/button";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const SCRIPT_ID = "article-jsonld";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

const BlogPost = () => {
  const { slug } = useParams();
  const post = findPost(slug);

  useDocumentMeta({
    title: post ? post.title : "Article",
    description: post ? post.excerpt : "ProsoftMarketplace blog — buying guides, comparisons, privacy tips.",
  });

  useEffect(() => {
    if (!post) return;
    const data = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.excerpt,
      image: post.cover,
      datePublished: post.date,
      author: { "@type": "Organization", name: "ProsoftMarketplace" },
      publisher: {
        "@type": "Organization",
        name: "ProsoftMarketplace",
        logo: { "@type": "ImageObject", url: "https://prosoftmarketplace.com/logo.png" },
      },
      mainEntityOfPage: `https://prosoftmarketplace.com/blog/${post.slug}`,
    };
    let s = document.getElementById(SCRIPT_ID);
    if (!s) {
      s = document.createElement("script");
      s.id = SCRIPT_ID;
      s.type = "application/ld+json";
      document.head.appendChild(s);
    }
    s.textContent = JSON.stringify(data);
    return () => {
      const existing = document.getElementById(SCRIPT_ID);
      if (existing) existing.remove();
    };
  }, [post]);

  if (!post) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Article not found</h1>
        <Button asChild className="mt-6 rounded-sm bg-emerald-500 text-white hover:bg-emerald-600">
          <Link to="/blog">Back to blog</Link>
        </Button>
      </div>
    );
  }

  const related = POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <article data-testid={`blog-post-${post.slug}`} className="bg-white">
      <div className="border-b border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <Link
            to="/blog"
            data-testid="back-to-blog"
            className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft className="h-3 w-3" /> Back to blog
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-600">
          {post.category}
        </div>
        <h1 className="mt-3 font-display text-4xl font-black leading-tight tracking-tighter text-slate-900 sm:text-5xl">
          {post.title}
        </h1>
        <div className="mt-5 flex flex-wrap items-center gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" /> {formatDate(post.date)}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" /> {post.readMinutes} min read
          </span>
          <span className="text-slate-400">·</span>
          <span>By {post.author}</span>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="relative">
          <img
            src={post.cover}
            alt={post.title}
            className="w-full border border-slate-900 object-cover"
          />
          <div className="absolute -bottom-3 -right-3 -z-10 h-full w-full bg-emerald-500" />
        </div>
      </div>

      {/* Body */}
      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-lg leading-relaxed text-slate-700">
          {post.body.map((block, i) => {
            if (block.type === "h2") {
              return (
                <h2
                  key={i}
                  className="mt-10 font-display text-2xl font-bold tracking-tight text-slate-900"
                >
                  {block.text}
                </h2>
              );
            }
            return (
              <p key={i} className="mt-4 text-base leading-relaxed text-slate-700">
                {block.text}
              </p>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-12 border-2 border-slate-900 bg-yellow-0 p-6 shadow-[8px_8px_0_0_rgba(16,185,129,1)]">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">
            Ready to lock things down?
          </div>
          <h3 className="mt-2 font-display text-2xl font-black tracking-tighter text-slate-900">
            Buy your antivirus in bulk and save.
          </h3>
          <p className="mt-2 text-sm text-slate-600">
            Huntheal at independent-reseller prices. Instant delivery, PayPal checkout.
          </p>
          <Button
            asChild
            data-testid="blog-cta-shop"
            className="mt-4 rounded-sm bg-slate-900 text-white hover:bg-slate-800"
          >
            <Link to="/products">
              Shop All Plans <ChevronRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Related */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
            Keep reading
          </div>
          <h2 className="mt-2 font-display text-2xl font-bold tracking-tight text-slate-900">
            Related articles
          </h2>
          <div className="mt-6 grid gap-6 md:grid-cols-3">
            {related.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="group flex flex-col border border-slate-200 bg-white transition-all hover:border-slate-900"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src={p.cover}
                    alt={p.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-5">
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-600">
                    {p.category}
                  </div>
                  <h3 className="mt-2 font-display text-base font-bold tracking-tight text-slate-900">
                    {p.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </article>
  );
};

export default BlogPost;
